import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild, ViewChildren } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import * as turf from '@turf/turf';
import * as _ from 'lodash';
import { orderBy, pick } from 'lodash';
import { Marker } from 'mapbox-gl';
import * as moment from 'moment';
import * as momenttimezone from 'moment-timezone';
import { ToastrService } from 'ngx-toastr';
import { Subject, concat, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { DialogService } from '../../dialog/dialog.service';
import { InspectorService } from '../../inspector/inspector.service';
import { MapboxComponent } from '../../mapbox/mapbox.component';
import { PolicyService } from '../../policy/policy.service';
import { QuestionService } from '../../question/question.service';
import { SettingService } from '../../setting/setting.service';
import { AuthService } from '../../shared/auth.service';
import { ComponentCanDeactivate } from '../../shared/can.deactivate.guard';
import { dateAsString } from '../../shared/date.helper';
import {
  Inspection,
  InspectionPurpose,
  InspectionVersion,
  Inspector,
  Policy,
  PolicyLocation,
  Vendor,
} from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { StateService } from '../../shared/state.service';
import { CustomValidators } from '../../shared/validation.patterns';
import { UserService } from '../../user/user.service';
import { VendorService } from '../../vendor/vendor.service';
import { VersionCloneModalComponent } from '../../version/version-clone-modal/version-clone-modal.component';
import { VersionService } from '../../version/version.service';
import { InspectionService } from '../inspection.service';
import { InspectionStatus } from '../inspection.status';
import { InspectionSendEmailComponent } from '../send.email/send.email.component';
@Component({
  selector: 'app-inspection-form',
  templateUrl: './inspection-form.component.html',
  styleUrls: ['./inspection-form.component.scss'],
})
export class InspectionFormComponent implements OnInit, ComponentCanDeactivate {
  @ViewChild('sendEmailModal', { static: true }) public sendEmailModal: InspectionSendEmailComponent;
  @ViewChild('cloneModal', { static: true }) public cloneModal: VersionCloneModalComponent;
  @ViewChild('inspectionNotes') public inspectionNotes;
  @ViewChildren('boxIframe') public boxIframe;
  @ViewChildren('pdfFile') public pdfFileInput;

  form: UntypedFormGroup;
  statusAsText: InspectionService['statusAsText'];
  InspectionStatus = InspectionStatus;
  InspectionPurpose = InspectionPurpose;

  activeTab: 'customer' | 'locations' | 'vendor' = 'customer';
  notesChangesTab: 'notes' | 'changes' = 'notes';
  showModal = false;

  inspection = {} as Inspection;
  autoUnassignedThreshold: number;
  canViewInspector = false;
  sendRemindersToInspectors = false;
  canEdit = false;
  canAssign = false;
  canMarkAsPaid = false;

  // make available to html
  dateAsString = dateAsString;
  forecasted = false;

  policies$ = of([]);
  policyInput$ = new Subject<string>();
  policyLoading = false;

  assignees = [];

  vendors$ = of([]);
  inspectors$ = of([]);
  vendorInput$ = new Subject<string>();
  vendorLoading = false;
  inspectorLoading = false;
  vendorInspectors: Inspector[] = [];
  viewMoreClicked: boolean = false;
  vendorViewMoreClicked: boolean = false;
  showVendorModal: boolean = false;
  selectedVendor: any = null;
  showInspectorModal: boolean = false;
  selectedInspector: any = null;
  inspectionProductType: string;
  inspectionProductTypeName: string;
  inspectors: Inspector[] = [];
  vendors: Vendor[] = [];
  policyLocations: PolicyLocation[] = [];
  policyLocationsLoading = false;
  selectedPolicyLocation: PolicyLocation | null = null;
  map: MapboxComponent;
  lastLocation;

  versions: InspectionVersion[] = [];
  version = {} as InspectionVersion;
  disableVersionButtons = true;

  creatingFolder = false;
  canSeeFiles = false;

  disableDelete = false;
  loading = false;
  noQuestions = false;
  vendorsLoaded = false;

  locale = {
    format: 'MM-DD-YYYY',
    displayFormat: 'MM-DD-YYYY',
  };
  futureTwoYears = new Date();
  pastTwoYears = new Date();
  uploadingPdf = false;
  pdfFormData: FormData;
  private loadInspectorsSubject = new Subject<number>();
  localTimezone: string;
  private isHydratingForm = false;

  // Bill.com / Mark as Paid
  showMarkAsPaidButton = false;
  pendingInvoiceIssued: boolean = false;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public inspectionService: InspectionService,
    public vendorService: VendorService,
    public policyService: PolicyService,
    public authService: AuthService,
    public userService: UserService,
    public questionService: QuestionService,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private settingService: SettingService,
    private dialog: DialogService,
    private versionService: VersionService,
    private sanitizer: DomSanitizer,
    private changeDetector: ChangeDetectorRef,
    private stateService: StateService,
    public inspectorService: InspectorService,
  ) {
    this.statusAsText = inspectionService.statusAsText;
    this.localTimezone = momenttimezone.tz.guess();
  }

  async ngOnInit() {
    this.pastTwoYears = new Date();
    this.pastTwoYears.setFullYear(this.pastTwoYears.getFullYear() - 2);
    this.pastTwoYears.setHours(0, 0, 0, 0);
    this.futureTwoYears.setFullYear(this.futureTwoYears.getFullYear() + 2);
    this.futureTwoYears.setHours(23, 59, 59, 999);

    this.form = this.fb.group({
      version: ['', [Validators.required]],
      purpose: ['New', [Validators.required]],
      hadSyncProblems: [false],
      contactName: ['', [Validators.required]],
      contactPhone: ['', [Validators.required, CustomValidators.phone]],
      contactEmail: ['', [Validators.required, Validators.email]],
      assignee: [''],
      policy: this.fb.group({
        id: [null],
        group: [null],
        active: [true],
        hadSyncProblems: [false],
        productType: [''],
        number: ['', [Validators.required]],
        effectiveDate: ['', [Validators.required]],
        customer: this.fb.group({
          id: [null],
          name: ['', [Validators.required]],
        }),
        broker: this.fb.group({
          id: [null],
          active: [true],
          name: ['', [Validators.required]],
          phone: ['', [Validators.required, CustomValidators.phone]],
          email: ['', [Validators.required, Validators.email]],
        }),
      }),
      locations: this.fb.group({
        locationId: [null, [Validators.required]],
        address: ['', [Validators.required]],
        city: ['', [Validators.required]],
        state: ['', [Validators.required]],
        zip: ['', [Validators.required]],
        dueDate: [''],
        forecasted: ['', []],
        items: this.fb.array(
          [],
          [
            (formArray: UntypedFormArray) => {
              const invalid = formArray.controls.find(c => c.value.quantity.invalid);
              if (invalid) {
                return { invalid_items: true };
              }
              return {};
            },
          ],
        ),
        notes: ['', []],
        lat: ['', [Validators.required]],
        lng: ['', [Validators.required]],
      }),
      vendor: this.fb.group({
        id: [null],
        name: ['', []],
      }),
      inspector: this.fb.group({
        id: [null],
        name: ['', []],
        email: ['', []],
        phone: ['', []],
      }),
    });
    this.setLocationDependentFieldsDisabled(true);
    this.setAssignmentFieldsDisabled(true);

    this.loadInspectorsSubject.pipe(debounceTime(100)).subscribe(vendorId => {
      this.loadInspectors(vendorId);
    });

    this.form.get('locations.dueDate').valueChanges.subscribe(val => {
      if (!val) {
        this.form.get('locations.forecasted').setValue(false);
        this.form.get('locations.forecasted').disable();
      } else {
        if (
          [
            InspectionStatus.NO_DATE_ASSIGNED,
            InspectionStatus.READY_TO_BE_ASSIGNED,
            InspectionStatus.WAITING,
            InspectionStatus.ASSIGNMENT_MADE,
          ].includes(this.inspection.status)
        ) {
          this.form.get('locations.forecasted').enable();
        }
      }
    });

    if (!this.form.get('locations.dueDate').value && InspectionStatus.NO_DATE_ASSIGNED) {
      this.form.get('locations.forecasted').setValue(false);
    }

    this.assignees = await this.userService.assignees();
    if (this.assignees.length > 0) {
      const currentUser = await this.userService.profile();
      const currentUserIndex = this.assignees.findIndex(a => a.id === currentUser.id);
      let signedInUser = null;
      if (currentUserIndex !== -1) {
        signedInUser = this.assignees.splice(currentUserIndex, 1)[0];
      }
      this.assignees = this.assignees.sort((a, b) => a.name.localeCompare(b.name));
      this.assignees = signedInUser ? [signedInUser, ...this.assignees] : [...this.assignees];
    }

    // Set default version
    this.versions = await this.versionService.list();
    const defaultVersion = this.versions.find(v => v.name === 'Poultry PLUS Inspection' && v.isValid);
    const validVersions = this.versions.filter(v => v.isValid);
    const selectedVersion = defaultVersion ? defaultVersion : validVersions[0];
    this.form.get('version').setValue(selectedVersion);
    await this.setupItems();

    this.form.get('version').valueChanges.subscribe(async newVersion => {
      if (!newVersion) {
        return;
      }
      await this.checkIfVersionHasQuestions(newVersion);
      setTimeout(async () => {
        await this.setupItems();
      }, 100);
    });

    this.route.queryParams.subscribe(async params => {
      if (params['versionName']) {
        const versionName = params['versionName'];
        const validVersions = this.versions.filter(v => v.isValid);
        const defaultVersion = this.versions.find(v => v.name === 'Poultry PLUS Inspection');
        const selectedVersion = versionName ? this.versions.find(v => v.name === versionName) : defaultVersion;
        this.form.get('version').setValue(selectedVersion || validVersions[0]);
      }
      if (params['duplicate']) {
        const duplicateId = parseInt(params['duplicate']);
        const insp = await this.inspectionService.get(duplicateId);
        const picked: Partial<Inspection> = pick(
          insp,
          'policy',
          'purpose',
          'forecasted',
          'address',
          'city',
          'zip',
          'state',
          'inspector',
          'vendor',
          'policyEffectiveDate',
          'location',
          'notes',
          'locationRelevance',
          'version',
          'versionId',
          'contactPhone',
          'contactName',
          'contactEmail',
          'dueDate',
        );
        picked.items = insp.items.map(i => ({
          id: null,
          inspectionId: null,
          ...pick(i, 'item', 'quantity', 'names', 'itemId'),
        }));
        this.inspection = picked;
        // Not awaited: setData re-applies the vendor/inspector lock rules after its first await,
        // and that must run after the enable() below or locked inspector fields become editable.
        const hydrated = this.setData(picked);
        this.form.enable();
        this.toastr.success('You are now creating a new Inspection');
        // This route reuses the component, so the map is still sized for the source inspection's
        // layout. Without an id the right column is gone and the map must re-fit the wider form.
        hydrated.then(() =>
          setTimeout(() => {
            this.map?.map?.resize();
            this.updateMapFromForm();
          }),
        );
      }
    });

    this.route.data.subscribe(async (data: { inspection: Inspection }) => {
      if (data.inspection) {
        this.inspection = data.inspection;
        this.urlcache = null;
        this.inspection.changes = await this.inspectionService.changes(data.inspection.id);
        this.form.get('version').setValue(this.versions.find(v => v.id === this.inspection.versionId));
        this.checkArchivedState();
        await this.loadAllInspectors();
        await this.setData(data.inspection);
      } else {
        if (!this.vendorsLoaded) {
          await this.loadAllVendors();
        }
      }
    });

    this.canAssign = await this.userService.hasPermissions([permissions.INSPECTION.CAN_ASSIGN]);
    this.canEdit = await this.userService.hasPermissions([permissions.INSPECTION.EDIT]);
    this.canViewInspector = await this.userService.hasPermissions([permissions.INSPECTOR.READ]);
    this.canMarkAsPaid = await this.userService.hasPermissions([permissions.INSPECTION.MARK_AS_PAID]);
    this.sendRemindersToInspectors = await this.settingService.sendRemindersDirectlyToInspectors();
    this.autoUnassignedThreshold = await this.settingService.autoUnassignThreshold();
    this.canSeeFiles = await this.userService.hasPermissions([permissions.BOX.READ]);

    if (!this.canAssign) {
      this.form.get('assignee').disable();
    }

    if (!this.canEdit) {
      this.form.disable();
    }

    this.checkArchivedState();
    if (this.inspection.id) {
      this.loadPolicies(this.inspection.location.policy);
    } else {
      this.loadPolicies();
    }

    if (!this.vendorsLoaded) {
      await this.loadAllInspectors();
      await this.loadAllVendors();
    }

    if (!this.hasSelectedLocation() || !this.form.get('vendor.id').value) {
      this.form.get('inspector').disable();
    }
  }

  async configureProductType() {
    const productType = this.form.get('policy.productType')?.value;
    if (productType && typeof productType === 'object' && 'name' in productType) {
      this.inspectionProductTypeName = productType.name;
    } else {
      this.inspectionProductTypeName = null;
    }
    this.inspectionProductType = productType;
    this.changeDetector.detectChanges();
    await this.filterVendorsByProductType();
  }

  scrollToFormSection(fieldName: string) {
    setTimeout(() => {
      document.getElementById(`${fieldName}-section`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  setActiveTab(tab: 'notes' | 'changes') {
    this.notesChangesTab = tab;
  }

  showAddNoteModal(): void {
    this.showModal = true;
    this.changeDetector.detectChanges();
  }

  hideAddNoteModal(): void {
    console.log('Hiding modal');
    this.showModal = false;
    this.changeDetector.detectChanges();
  }

  async onNoteCreated(createdNote: any): Promise<void> {
    this.toastr.success('Note saved successfully!');
    this.addNewNoteToList(createdNote);
    this.hideAddNoteModal();
    this.inspection.changes = await this.inspectionService.changes(this.inspection.id);
    this.changeDetector.detectChanges();
  }

  private addNewNoteToList(createdNote: any): void {
    if (this.inspectionNotes) {
      this.inspectionNotes.addNewNote(createdNote);
    }
  }

  isLocationsValid() {
    const infos = this.form.get('locations.items').value;
    return this.form.get('locations').valid && !infos.find(i => i.quantity.invalid);
  }

  hasLocations(): boolean {
    return !this.policyLocationsLoading && this.policyLocations.length > 0;
  }

  hasSelectedLocation(): boolean {
    return !!this.form?.get('locations.locationId')?.value && !!this.selectedPolicyLocation;
  }

  @HostListener('window:focus')
  async refreshLocationsIfNeeded() {
    // No need to check document.hidden here; window:focus only fires when the window gains focus
    // and at that point document.hidden would be false.
    const policyId = this.form?.get('policy.id')?.value;
    if (!policyId) {
      return;
    }

    if (!this.hasLocations()) {
      await this.loadPolicyLocations(policyId);
      this.syncPolicyLocationFormState();
    }
  }

  isPolicyValid() {
    return this.form.get('policy')?.disabled || this.form.get('policy')?.valid;
  }

  isBrokerValid() {
    const broker = this.form.get('policy.broker').getRawValue();
    return !!(broker?.name && broker?.email && broker?.phone);
  }

  canSaveInspectionSetup() {
    return this.hasSelectedLocation();
  }

  areNamesValid() {
    return this.form
      .get('locations.items')
      .value.reduce((valid, curr) => valid && !curr.namesInputs?.some(input => input.invalid), true);
  }

  daysSince(date) {
    return Math.round((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
  }

  // Version/items logic
  async refreshVersions() {
    const currentVersionId = this.form.get('version').value?.id;
    this.versions = await this.versionService.list();
    const newVersion = this.versions.find(v => v.id === currentVersionId);
    this.form.get('version').setValue(newVersion);
  }

  async setupItems() {
    const formArray = this.form.get('locations.items') as UntypedFormArray;
    const newVersion = this.form.get('version').value;
    const currentItems = formArray.controls.map(x => {
      const customNames = x.value.customNames?.getRawValue?.() ?? x.value.customNames?.value;
      return {
        item: x.value.item.name,
        quantity: x.value.quantity?.getRawValue?.() ?? x.value.quantity?.value,
        customNames,
        names: customNames ? (x.value.namesInputs || []).map(input => input?.getRawValue?.() ?? input?.value) : [],
      };
    });
    if (newVersion?.id === this.inspection.versionId) {
      formArray.clear();
      // First load, set quantities from inspection

      this.inspection.items.forEach(info => {
        const quantity = this.fb.control(info.quantity, {
          updateOn: 'blur',
          validators: [
            Validators.required,
            Validators.min(info.item.minInstances),
            Validators.max(info.item.maxInstances),
          ],
        });

        const customNames = this.fb.control(info.names && info.names.length);

        const newInfo = this.fb.control({
          ...info,
          quantity,
          customNames,
        });

        quantity.valueChanges.subscribe(() => {
          this.form.get('locations.items').updateValueAndValidity();
          this.setItemNames(newInfo);
        });
        this.setItemNames(newInfo);

        formArray.push(newInfo);
      });
    } else {
      if (newVersion) {
        formArray.clear();

        const items = await this.versionService.items(newVersion.id);
        items.sort((a, b) => a.order - b.order);

        items.forEach(item => {
          const itemMatch = currentItems.find(i => i.item === item.name);

          const quantity = this.fb.control(itemMatch ? itemMatch.quantity : item.minInstances, [
            Validators.required,
            Validators.min(item.minInstances),
            Validators.max(item.maxInstances),
          ]);

          const customNames = this.fb.control(!!itemMatch?.customNames);

          const newInfo = this.fb.control({
            item,
            quantity,
            customNames,
            names: itemMatch?.names ?? [],
          });

          quantity.valueChanges
            .pipe(
              debounceTime(300),
              distinctUntilChanged(),
              switchMap(value => {
                return of(value);
              }),
            )
            .subscribe(() => {
              this.form.get('locations.items').updateValueAndValidity();
              this.setItemNames(newInfo);
            });
          this.setItemNames(newInfo);

          formArray.push(newInfo);
        });
      }
    }

    if (this.inspection.status === InspectionStatus.ASSIGNMENT_MADE || this.form.get('version').disabled) {
      formArray.controls.forEach(control => this.setLocationItemControlsDisabled(control, true));
    }
  }

  setCustomVersion(inspectionVersion: InspectionVersion) {
    this.versions.push(inspectionVersion);
    this.versions = orderBy(this.versions, ['name'], ['asc']);
    this.form.get('version').setValue(inspectionVersion);
  }

  setItemNames(infoControl) {
    const info = infoControl.value;

    const oldInputs = info.namesInputs;
    info.namesInputs = [];
    const quantity = info.quantity.value;

    // application Crash check : if user enter more maxInstances
    if (quantity <= info.item?.maxInstances) {
      for (let i = BigInt(0); i < quantity; i++) {
        const index = Number(i);

        if (oldInputs && oldInputs[index]) {
          info.namesInputs.push(oldInputs[index]);
        } else {
          const control = this.fb.control((info.names && info.names[index]) || '');
          (control as any).itemName = info.item.name;
          info.namesInputs.push(control);
        }
      }
    }
  }

  validateItemName(control: UntypedFormControl) {
    if (this.inspection.items) {
      const allItemNames = this.inspection.items.reduce((acc, curr) => [...acc, curr.item.name], []);

      if (
        allItemNames
          .filter(name => name !== (control as any).itemName)
          .some(name => control.value.toLowerCase().includes(name.toLowerCase()))
      ) {
        return { invalid_name: true };
      }
    }
    return {};
  }

  customizeVersion() {
    const version = this.form.get('version').value;
    this.version = this.versions.find(v => v.id === version.id);
    setTimeout(() => {
      this.cloneModal.show();
    }, 0);
  }

  async checkIfVersionHasQuestions(version: InspectionVersion) {
    const questionOfCurrentVersion = await this.questionService.listByInspectionVersion(version.id);
    this.noQuestions = questionOfCurrentVersion.length === 0;
    if (questionOfCurrentVersion.length === 0) {
      await this.dialog.alert().show({
        message:
          "The version selected doesn't have any questions. Please change the inspection version so you can save this inspection.",
      });
    }
  }

  // Set data and submit
  async setData(data: Inspection) {
    this.isHydratingForm = true;
    if (data?.policy) {
      data.policy.effectiveDate = new Date(data.policy.effectiveDate);
    }

    this.form.get('hadSyncProblems').enable();

    const versionExists = this.versions.find(v => v.id === data.versionId);
    if (!versionExists) {
      this.versions = orderBy(this.versions, ['name'], ['asc']);
      this.versions.unshift(data.version);
    }

    const newVersion = _.find(this.versions, function (versions: any) {
      return versions.id == data.version.id;
    });

    const contactName = data.contactName;
    const contactPhone = data.contactPhone;
    const contactEmail = data.contactEmail;

    // we are patching the form below so items form array Control should be clear before
    const arrayControl = this.form.get('locations.items') as FormArray;
    while (arrayControl.length !== 0) {
      arrayControl.removeAt(0);
    }

    // Set data
    this.form.patchValue({
      contactName: contactName,
      contactPhone: contactPhone,
      contactEmail: contactEmail,
      policy: {
        ...data.policy,
        productType: data.location.policy?.productType,
        customer: data.location.policy?.customer,
        broker: data.location.policy?.broker,
        group: data.location.policy?.group,
      },
      locations: {
        ...pick(data, 'dueDate', 'notes', 'items', 'forecasted'),
        address: data.location?.address,
        city: data.location?.city,
        state: data.location?.state,
        zip: data.location?.zip,
        locationId: data.location?.number,
        lat: data.location?.coordinates && data.location?.coordinates[1],
        lng: data.location?.coordinates && data.location?.coordinates[0],
      },
      version: newVersion,
      purpose: data.purpose,
      hadSyncProblems: data.hadSyncProblems,
    });

    this.changeDetector.detectChanges();

    if (data.policy?.id) {
      await this.loadPolicyLocations(data.policy.id);
      this.changeDetector.detectChanges();
      if (data.location?.id) {
        this.selectedPolicyLocation = this.policyLocations.find(l => l.id === data.location.id) || data.location;
        if (data.location.number) {
          this.form.get('locations.locationId').setValue(data.location.number);
        }
      }
    }

    if (data.dueDate) {
      this.form.get('locations.dueDate').setValue(data.dueDate);
    }

    if (data.vendor) {
      this.form.get('vendor').patchValue({
        id: data.vendor.id,
        name: data.vendor.name,
      });
      this.changeDetector.markForCheck();
    } else {
      this.form.get('vendor').patchValue({ id: null, name: null });
      this.changeDetector.markForCheck();
    }
    if (data.assignee) {
      this.form.get('assignee').patchValue({
        id: data.assignee.id,
        name: data.assignee.name,
      });
      this.changeDetector.markForCheck();
    }

    if (data.inspector) {
      this.loadInspectorsSubject.next(this.form.get('vendor.id').value);
      this.form.get('inspector').patchValue({
        id: data.inspector.id,
        name: data.inspector.name,
        email: data.inspector.email,
        phone: data.inspector.phone,
      });
      this.changeDetector.markForCheck();
    } else {
      this.form.get('inspector').patchValue({ id: null, name: null, email: null, phone: null });
      this.changeDetector.markForCheck();
    }

    if (Array.isArray(data.coordinates)) {
      this.setMarker(data.coordinates);
    }

    await this.setupItems();

    // Disable fields
    this.disableDelete = false;
    this.disableVersionButtons = true;
    switch (data.status) {
      case InspectionStatus.NO_DATE_ASSIGNED:
      case InspectionStatus.READY_TO_BE_ASSIGNED:
      case InspectionStatus.WAITING:
        this.disableVersionButtons = false;
        break;

      case InspectionStatus.ASSIGNMENT_MADE:
        this.disableFieldsForAssignmentMade();
        break;

      case InspectionStatus.REJECTED:
        this.form.disable();
        this.disableFormExcept(['vendor', 'hadSyncProblems', 'assignee']);
        this.disableDelete = true;
        break;

      case InspectionStatus.NEEDS_REVIEW:
      case InspectionStatus.ACCEPTED:
      case InspectionStatus.DEPRECATED_COMPLETED:
      case InspectionStatus.INVALID_STATE:
      case InspectionStatus.INVALID_SYNC:
        this.form.disable();
        this.disableFormExcept(['hadSyncProblems', 'assignee']);
        this.disableDelete = true;
        break;
    }
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.isHydratingForm = false;
    this.syncPolicyLocationFormState();

    // Initialize pending invoice issued from backend value
    this.pendingInvoiceIssued = this.inspection.invoiceIssued || false;

    // Check if Mark as Paid button should be shown
    await this.checkBillComVisibility();
  }

  async checkBillComVisibility() {
    try {
      const setting = await this.settingService.findByKey('inspection_id_bill_com');
      if (setting && setting.value && this.inspection.vendor) {
        this.showMarkAsPaidButton = setting.value === String(this.inspection.vendor.id);
      } else {
        this.showMarkAsPaidButton = false;
      }
    } catch (error) {
      console.error('Error fetching Bill.com setting:', error);
      this.showMarkAsPaidButton = false;
    }
  }

  disableFieldsForAssignmentMade() {
    this.form.get('locations.items').disable();
    this.form.get('version').disable();
    this.form.get('purpose').disable();
    this.form.get('policy').disable();
  }

  async submit() {
    this.loading = true;
    const { locations } = this.form.controls;

    const controls = Object.assign({}, (locations as any).controls);

    const value = {} as any;

    for (const c of Object.keys(controls)) {
      controls[c].markAsTouched();
      value[c] = controls[c].value;
    }

    if (!this.form.valid) {
      return;
    }

    const items = this.form.get('locations.items').value.map(info => ({
      id: info.id,
      inspectionId: info.inspectionId,
      item: info.item,
      itemId: info.itemId,
      quantity: info.quantity.value,
      names: info.customNames.value ? info.namesInputs.map(input => input.value) : [],
    }));

    const selectedLocation = this.policyLocations.find(l => l.number === value.locationId);

    const location = {
      ...selectedLocation,
      coordinates: [this.form.get('locations.lng').value, this.form.get('locations.lat').value],
      address: this.form.get('locations.address').value,
      city: this.form.get('locations.city').value,
      state: this.form.get('locations.state').value,
      zip: this.form.get('locations.zip').value,
      contactName: this.form.get('contactName').value,
      contactEmail: this.form.get('contactEmail').value,
      contactPhone: this.form.get('contactPhone').value,
      policy: this.form.get('policy').getRawValue(),
    };

    const inspector = {
      id: this.form.get('inspector.id').value,
      name: this.form.get('inspector.name').value,
      email: this.form.get('inspector.email').value,
      phone: this.form.get('inspector.phone').value,
    };

    const vendorEmail = this.vendors.find(vendor => vendor.id === this.form.get('vendor.id').value)?.email || '';

    const updatedVendor = {
      id: this.form.get('vendor.id').value,
      name: this.form.get('vendor.name').value,
      email: vendorEmail,
    };

    const inspectionDTO = {
      ...value,
      dueDate: value.dueDate ? value.dueDate : null,
      forecasted: value.forecasted,
      policyEffectiveDate: value.policyEffectiveDate ? new Date(value.policyEffectiveDate) : null,
      vendor: this.form.get('vendor.id').value ? updatedVendor : null,
      inspector: this.form.get('inspector.id').value ? inspector : null,
      policy: this.form.get('policy').getRawValue(),
      version: this.form.get('version').value,
      purpose: this.form.get('purpose').value,
      contactName: this.form.get('contactName').value,
      contactEmail: this.form.get('contactEmail').value,
      contactPhone: this.form.get('contactPhone').value,
      customerName: this.form.get('policy.customer.name').value,
      hadSyncProblems: this.form.get('hadSyncProblems').value,
      assignee: pick(this.form.get('assignee').value, 'id'),
      items,
      location,
      invoiceIssued: this.pendingInvoiceIssued,
    };

    const shouldWarnUnassign =
      (inspectionDTO.vendor || inspectionDTO.inspector) &&
      value.dueDate &&
      moment(value.dueDate).diff(moment(Date.now()).add(this.autoUnassignedThreshold, 'days'), 'days') > 0;

    const unassignedDTO = {
      ...inspectionDTO,
      vendor: null,
      vendorId: null,
      inspector: null,
      inspectorId: null,
    };

    if (this.inspection.id) {
      try {
        if (shouldWarnUnassign) {
          this.dialog.confirm().show({
            message: `This inspection will get unassigned as it's more than ${this.autoUnassignedThreshold} days away. Are you sure?`,
            confirmed: async () => {
              this.inspection.vendor = null;
              this.inspection.inspector = null;
              this.inspection = await this.inspectionService.edit(this.inspection.id, unassignedDTO);
              await this.setData(this.inspection);
              this.inspection.changes = await this.inspectionService.changes(this.inspection.id);
              this.toastr.success('Inspection saved!');
              this.loading = false;
            },
            canceled: () => {
              this.loading = false;
            },
          });
        } else {
          this.inspection = await this.inspectionService.edit(this.inspection.id, inspectionDTO);
          await this.setData(this.inspection);
          this.toastr.success('Inspection saved!');
          this.loading = false;
          this.inspection.changes = await this.inspectionService.changes(this.inspection.id);
        }
      } catch (e) {
        console.log('error edit', e);
        this.loading = false;
      }
    } else {
      try {
        if (shouldWarnUnassign) {
          this.dialog.confirm().show({
            message: `This inspection will get unassigned as it's more than ${this.autoUnassignedThreshold} days away. Are you sure?`,
            confirmed: async () => {
              const inspection = await this.inspectionService.create(unassignedDTO);
              this.toastr.success('Inspection saved!');
              this.loading = false;
              this.inspection = inspection;
              this.changeDetector.detectChanges();
              await this.setData(inspection);
              this.router.navigate([`/inspection/form/`, inspection.id]);
            },
            canceled: () => {
              this.loading = false;
            },
          });
        } else {
          const inspection = await this.inspectionService.create(inspectionDTO);
          this.toastr.success('Inspection saved!');
          this.loading = false;
          this.inspection = inspection;
          this.changeDetector.detectChanges();
          await this.setData(inspection);
          this.router.navigate([`/inspection/form/`, inspection.id]);
        }
      } catch (e) {
        console.log('error create', e);
        this.loading = false;
      }
    }
  }

  // Policies logic
  private loadPolicies(existingPolicy: any = null) {
    this.policies$ = concat(
      // If existingPolicy is available, start with it, otherwise start with an empty array
      of(existingPolicy ? [existingPolicy] : []),
      this.policyInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.policyLoading = true)),
        switchMap(term => {
          if (term === null || term.trim() === '') {
            return of([]);
          } else {
            return this.policyService.list({ search: term }).pipe(
              catchError(() => of([])),
              map(response => {
                if (Array.isArray(response) && response.length > 0) {
                  return response[0].map(p => ({
                    ...p,
                    effectiveDate: new Date(p.effectiveDate),
                  }));
                }
                return [];
              }),
            );
          }
        }),
        tap(() => (this.policyLoading = false)),
      ),
    );
  }

  policyTrackByFn(policy: Policy) {
    return policy.id;
  }

  async onPolicySelect(policy: any) {
    if (policy && typeof policy === 'object') {
      this.form.get('policy').patchValue({
        ...policy,
        broker: policy.broker || {},
        customer: policy.customer || {},
        group: policy.group || null,
      });
      if (policy.id) {
        await this.loadPolicyLocations(policy.id);
      } else {
        this.policyLocationsLoading = false;
        this.policyLocations = [];
      }
      this.clearLocationAndContact();
    } else {
      this.form.get('policy')?.reset();
      this.policyLocations = [];
      this.selectedPolicyLocation = null;
      this.clearLocationAndContact();
    }
    this.configureProductType();
    this.syncPolicyLocationFormState();
  }

  async loadPolicyLocations(policyId: number) {
    this.policyLocationsLoading = true;
    try {
      this.policyLocations = await this.policyService.getLocations(policyId);
    } catch {
      this.policyLocations = [];
    } finally {
      this.policyLocationsLoading = false;
    }
  }

  onPolicyLocationSelect(location: PolicyLocation | null) {
    if (!location) {
      this.clearLocationAndContact();
      this.setAssignmentFieldsDisabled(true);
      return;
    }
    this.selectedPolicyLocation = location;
    const [lng, lat] = location.coordinates || [];
    this.form.get('locations').patchValue({
      address: location.address,
      city: location.city,
      state: location.state,
      zip: location.zip,
      lat,
      lng,
    });

    this.form.patchValue({
      contactName: location.contactName,
      contactEmail: location.contactEmail,
      contactPhone: location.contactPhone,
    });

    if (lat && lng) this.setMarker([lng, lat]);
    this.setAssignmentFieldsDisabled(false);
  }

  clearLocationAndContact() {
    this.selectedPolicyLocation = null;
    this.form.get('locations').patchValue({
      locationId: null,
      address: '',
      city: '',
      state: '',
      zip: '',
      lat: '',
      lng: '',
    });
    this.form.patchValue({
      contactName: '',
      contactEmail: '',
      contactPhone: '',
    });

    // reset map to default view
    this.lastLocation = null;
    this.map?.jumpTo({ zoom: 14, center: [-122.41, 37.75] });
  }

  private syncPolicyLocationFormState() {
    if (!this.form || this.isHydratingForm || !this.canEdit || this.inspection.archived) {
      return;
    }

    const isSetupEditable =
      !this.inspection.id ||
      [
        InspectionStatus.NO_DATE_ASSIGNED,
        InspectionStatus.READY_TO_BE_ASSIGNED,
        InspectionStatus.WAITING,
        InspectionStatus.ASSIGNMENT_MADE,
      ].includes(this.inspection.status);

    if (!isSetupEditable) {
      return;
    }

    const canUseLocationFields = this.isPolicyValid() && this.hasLocations();
    const locationLockedByStatus = this.inspection.status === InspectionStatus.ASSIGNMENT_MADE;

    this.setLocationDependentFieldsDisabled(!canUseLocationFields, locationLockedByStatus);
    this.setAssignmentFieldsDisabled(!this.hasSelectedLocation());
  }

  private setLocationDependentFieldsDisabled(disabled: boolean, keepVersionLocked = false) {
    const controls = [
      { name: 'version', keepLocked: true },
      { name: 'purpose', keepLocked: true },
      { name: 'locations.locationId' },
    ];

    controls.forEach(({ name, keepLocked }) => {
      const shouldDisable = disabled || (keepVersionLocked && keepLocked);
      this.setControlDisabled(name, shouldDisable);
    });
    this.setLocationItemsDisabled(disabled || keepVersionLocked);
  }

  private setAssignmentFieldsDisabled(disabled: boolean) {
    ['vendor', 'inspector', 'assignee'].forEach(controlName => this.setControlDisabled(controlName, disabled));

    if (!disabled) {
      if (!this.canAssign) {
        this.form.get('assignee').disable();
      }

      if (!this.form.get('vendor.id').value || this.inspection.status === InspectionStatus.ASSIGNMENT_MADE) {
        this.form.get('inspector').disable();
      } else {
        this.form.get('inspector').enable();
        this.form.get('inspector.email').disable();
        this.form.get('inspector.phone').disable();
      }
    }
  }

  private setLocationItemsDisabled(disabled: boolean) {
    const formArray = this.form.get('locations.items') as UntypedFormArray;
    if (!formArray) {
      return;
    }

    if (disabled) {
      formArray.disable({ emitEvent: false });
    } else {
      formArray.enable({ emitEvent: false });
    }

    formArray.controls.forEach(control => this.setLocationItemControlsDisabled(control, disabled));
  }

  private setLocationItemControlsDisabled(itemControl: AbstractControl, disabled: boolean) {
    const info = itemControl.value;
    if (!info) {
      return;
    }

    [info.quantity, info.customNames, ...(info.namesInputs || [])].forEach(nestedControl => {
      if (!nestedControl) {
        return;
      }

      const isCustomNamesControl =
        nestedControl === info.customNames || (info.namesInputs || []).includes(nestedControl);
      if (
        disabled &&
        isCustomNamesControl &&
        this.inspection?.id &&
        this.inspection.status >= InspectionStatus.ASSIGNMENT_MADE
      ) {
        return;
      }

      if (disabled) {
        nestedControl.disable({ emitEvent: false });
      } else {
        nestedControl.enable({ emitEvent: false });
      }
    });
  }

  private setControlDisabled(controlName: string, disabled: boolean) {
    const control = this.form.get(controlName);
    if (!control) {
      return;
    }

    if (disabled) {
      control.disable();
    } else {
      control.enable();
    }
  }

  async loadAllVendors(loadFiltered: boolean = true) {
    if (this.vendorsLoaded && loadFiltered) {
      console.warn('Skipping vendors reload: Already loaded.');
      return;
    }
    this.vendorLoading = true;
    this.vendorsLoaded = false;
    const filters: any = { isPagination: 0 };

    if (!this.inspectors || this.inspectors.length === 0) {
      await this.loadAllInspectors();
    }

    try {
      const response: any = await this.vendorService.list(filters).toPromise();
      const data = response[0];

      this.vendors = this.sortVendors(Array.isArray(data) ? data : [data]);
      this.vendors$ = of(this.vendors);
      this.vendorsLoaded = true;

      if (loadFiltered) {
        await this.filterVendorsByProductType();
      } else {
        this.vendorViewMoreClicked = true;
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      this.vendors = [];
      this.vendors$ = of([]);
      this.vendorsLoaded = false;
    } finally {
      this.vendorLoading = false;
    }
  }

  private sortVendors(vendors: any[]): any[] {
    return vendors.sort((a, b) => a.name.localeCompare(b.name));
  }

  async loadAllInspectors() {
    this.inspectorLoading = true;
    const filters: any = { isPagination: 0 };
    try {
      const response: any = await this.inspectorService.list(filters).toPromise();
      const data = response[0];
      if (Array.isArray(data)) {
        this.inspectors = data;
      } else {
        this.inspectors = [data];
      }
    } catch (error) {
      console.error('Error fetching inspectors:', error);
      this.inspectors = [];
    } finally {
      this.inspectorLoading = false;
    }
  }

  async filterVendorsByProductType() {
    if (!this.inspectionProductTypeName || !this.inspectors || !this.vendors) {
      return;
    }
    const filteredVendors = this.vendors.filter(vendor => {
      const vendorInspectors = this.inspectors.filter(inspector => inspector.vendor?.id === vendor.id);
      const hasMatchingInspector = vendorInspectors.some(inspector => {
        if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
          return false;
        }
        const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
        return productTypesArray.includes(this.inspectionProductTypeName);
      });
      return hasMatchingInspector;
    });

    if (filteredVendors.length === this.vendors.length) {
      this.vendorViewMoreClicked = true;
    } else {
      this.vendorViewMoreClicked = false;
    }
    filteredVendors.sort((a, b) => a.name.localeCompare(b.name));
    this.vendors$ = of(filteredVendors);
    if (this.inspectors && this.form.get('vendor.id').value) {
      this.loadInspectorsSubject.next(this.form.get('vendor.id').value);
    }
  }

  async loadVendorInspectors(viewMore: boolean = false) {
    const vendorId = this.form.get('vendor.id').value;
    if (!vendorId) {
      console.error('Vendor ID is not defined.');
      this.form.get('inspector').disable();
      return;
    }

    this.vendorInspectors = await this.vendorService
      .inspectors(
        vendorId,
        '',
        [this.getCoordinateValue('locations.lng'), this.getCoordinateValue('locations.lat')].toString(),
      )
      .toPromise();

    if (!viewMore) {
      if (!this.inspectionProductTypeName) {
        await this.configureProductType();
        if (!this.inspectionProductTypeName) return console.error('Inspection Product Type Name is not defined.');
      }

      this.vendorInspectors = this.vendorInspectors.filter(inspector => {
        if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
          return false;
        }
        const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
        return productTypesArray.includes(this.inspectionProductTypeName);
      });
    }

    const inspectorId = this.form.get('inspector.id').value;
    if (inspectorId) {
      const exists = this.vendorInspectors.find(i => i.id === inspectorId);
      if (!exists) {
        this.form.get('inspector').setValue({
          id: null,
          name: '',
          email: '',
          phone: '',
        });
      }
    }
    this.form.get('inspector').enable();
    this.form.get('inspector.email').disable();
    this.form.get('inspector.phone').disable();
  }

  viewMoreInspectors() {
    this.viewMoreClicked = true;
    this.loadVendorInspectors(true);
  }

  async clearVendor() {
    this.vendorsLoaded = false;
    await this.loadAllInspectors();
    await this.loadAllVendors();
    this.vendorInspectors = [];
    this.form.get('vendor').setValue({
      id: null,
      name: '',
    });
    this.form.get('inspector').setValue({
      id: null,
      name: '',
      email: '',
      phone: '',
    });
    this.form.get('inspector').disable();
  }

  vendorTrackByFn(vendor: Vendor) {
    return vendor.id;
  }

  async onVendorSelect(vendor: any) {
    this.viewMoreClicked = false;
    if (typeof vendor === 'string') {
      this.form.get('vendor').patchValue({ name: vendor });
    } else if (typeof vendor === 'object' && vendor.id) {
      if (this.vendorViewMoreClicked && !this.vendorHasMatchingInspectors(vendor)) {
        this.form.get('inspector').enable();
        this.selectedVendor = vendor;
        this.openModal();
      } else {
        this.selectVendor(vendor);
      }
    }
    this.form.get('inspector').enable();
  }

  async onAssigneeSelect(admin: any) {
    this.selectAssignee(admin);
  }

  async selectAssignee(admin: any) {
    this.form.get('assignee').patchValue({
      id: admin.id,
      name: admin.name,
    });
  }

  clearAssignee() {
    this.form.get('assignee').setValue(null);
  }

  openModal() {
    this.showVendorModal = true;
    this.vendorViewMoreClicked = false;
  }

  onConfirm() {
    this.vendorViewMoreClicked = true;
    this.showVendorModal = false;
    this.selectVendor(this.selectedVendor);
  }

  onCancel() {
    this.clearVendor();
    this.showVendorModal = false;
  }

  async selectVendor(vendor: any) {
    this.form.get('vendor').patchValue({
      id: vendor.id,
      name: vendor.name,
    });
    await this.loadVendorInspectors();
  }

  vendorHasMatchingInspectors(vendor: any): boolean {
    const vendorInspectors = this.inspectors.filter(inspector => inspector.vendor?.id === vendor.id);
    return vendorInspectors.some(inspector => {
      if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
        return false;
      }
      const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
      return productTypesArray.includes(this.inspectionProductTypeName);
    });
  }

  //Inspectors logic
  async loadInspectors(vendorId: number) {
    this.inspectorLoading = true;
    const location = [this.form.get('locations.lng').value, this.form.get('locations.lat').value].toString();
    this.vendorInspectors = await this.vendorService.inspectors(vendorId, '', location).toPromise();

    const currentInspector = this.form.get('inspector')?.value;

    if (currentInspector && currentInspector.id) {
      const exists = this.vendorInspectors.some(inspector => inspector.id === currentInspector.id);

      if (!exists) {
        const results = await this.inspectorService.getInspectorWithMiles({
          id: currentInspector.id,
          distanceTo: location,
        });
        this.vendorInspectors = [...this.vendorInspectors, results];
      }
    }
    this.inspectorLoading = false;
  }

  inspectorTrackByFn(inspector: Inspector) {
    return inspector.id;
  }

  onInspectorSelect(inspector: any) {
    if (typeof inspector === 'string') {
      this.form.get('inspector').patchValue({ name: inspector });
    } else if (typeof inspector === 'object' && inspector.id) {
      if (!this.inspectorHasMatchingProductType(inspector)) {
        this.selectedInspector = inspector;
        this.selectInspector(inspector);
        this.showInspectorModal = true;
      } else {
        this.selectInspector(inspector);
      }
    }
  }

  onInspectorCancel() {
    this.clearInspector();
    this.showInspectorModal = false;
  }

  onInspectorConfirm() {
    this.showInspectorModal = false;
    this.selectInspector(this.selectedInspector);
  }

  selectInspector(inspector: any) {
    this.form.get('inspector').patchValue({
      id: inspector.id,
      name: inspector.name,
      email: inspector.email,
      phone: inspector.phone,
    });
  }

  inspectorHasMatchingProductType(inspector: any): boolean {
    if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
      return false;
    }
    const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
    return productTypesArray.includes(this.inspectionProductTypeName);
  }

  async clearInspector() {
    this.viewMoreClicked = false;
    await this.loadVendorInspectors();
    this.form.get('inspector').setValue({
      id: null,
      name: '',
      email: '',
      phone: '',
    });
  }

  // Map logic
  getCoordinatesFromForm() {
    const lat = this.getCoordinateValue('locations.lat');
    const lng = this.getCoordinateValue('locations.lng');
    return [lat, lng];
  }

  isValidCoordinate(coordinate: number) {
    return coordinate >= -180 && coordinate <= 180;
  }

  updateMapFromForm() {
    const [lat, lng] = this.getCoordinatesFromForm();
    if (this.isValidCoordinate(lat) && this.isValidCoordinate(lng)) {
      this.setMarker([lng, lat]);
    }
  }

  getCoordinateValue(formControlName: string) {
    const value = this.form.get(formControlName).value;
    return typeof value === 'number' ? value : parseFloat((value + '').replace(',', '.'));
  }

  setMarker(center?: [number, number]) {
    if (!this.map) {
      return;
    }
    try {
      this.lastLocation = center || this.lastLocation;
      if (!this.lastLocation) {
        return;
      }
      const marker = this.createMarker(this.lastLocation);
      this.addMarkerToMap(marker);
      this.addCircleToMap(this.lastLocation);
    } catch (e) {}
  }

  setMap(mapbox: MapboxComponent) {
    this.map = mapbox;
    const lat = this.form.get('locations.lat').value;
    const lng = this.form.get('locations.lng').value;
    if (lat && lng) {
      this.setMarker([lng, lat]);
    }
  }

  styleLoaded() {
    if (!this.map.map.getLayer('circleLayer') && this.lastLocation) {
      this.setMarker();
    }
  }
  createMarker(location: [number, number]) {
    return new Marker({ draggable: false }).setLngLat(location);
  }

  addMarkerToMap(marker: Marker) {
    this.map.removeMarkers();
    this.map.addMarker(marker);
    this.map.map.resize();
    this.map.jumpTo({
      zoom: 14,
      center: this.lastLocation,
    });
  }

  addCircleToMap(center: [number, number]) {
    // remove old layer
    if (this.map.map.getLayer('circleLayer')) {
      this.map.map.removeLayer('circleLayer');
      this.map.map.removeSource('circleSource');
    }
    const circle = turf.circle(center, 0.5, { steps: 64, units: 'miles' });
    this.map.map.addSource('circleSource', { type: 'geojson', data: circle });
    this.map.map.addLayer({
      id: 'circleLayer',
      type: 'fill',
      source: 'circleSource',
      paint: {
        'fill-color': 'white',
        'fill-opacity': 0.3,
      },
    });
  }

  // Notes
  handleNoteClicked() {
    this.scrollToFormSection('locations');
    setTimeout(() => {
      const notesSection = document.getElementById('note');
      if (notesSection) {
        notesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 0);
  }

  //Attachments
  addAttachment() {
    window.open(
      `https://agribusinessriskunderwriters.app.box.com${this.inspection.boxFileRequestUrl}`,
      '_blank',
      'width=750,height=750',
    );
  }

  async setupInspectionFolder() {
    this.creatingFolder = true;
    this.inspection = await this.inspectionService.setupInspectionFolder(this.inspection.id);
  }

  urlcache;
  get boxUrl() {
    if (!this.urlcache) {
      this.urlcache = this.sanitizer.bypassSecurityTrustResourceUrl(`/inspection/box/viewer/${this.inspection.id}`);
    }
    return this.urlcache;
  }

  // Inspection delete, archive, restore
  async restore(ev: Event) {
    ev.preventDefault();
    this.dialog.confirm().show({
      message: `Do you really want to restore this inspection?`,
      confirmed: async () => {
        this.inspection = await this.inspectionService.restore(this.inspection.id);
        this.inspection.changes = await this.inspectionService.changes(this.inspection.id);
        this.toastr.success('Inspection Restored!');
        if (
          [
            InspectionStatus.NO_DATE_ASSIGNED,
            InspectionStatus.READY_TO_BE_ASSIGNED,
            InspectionStatus.WAITING,
            InspectionStatus.ASSIGNMENT_MADE,
          ].includes(this.inspection.status)
        ) {
          this.form.enable();
        }
      },
    });
  }

  async archive(ev: Event) {
    ev.preventDefault();
    this.dialog.confirm().show({
      message: `Do you really want to archive this inspection?`,
      confirmed: async () => {
        console.log('Archiving inspection:', this.inspection);
        this.inspection = await this.inspectionService.archive(this.inspection.id);
        this.inspection.changes = await this.inspectionService.changes(this.inspection.id);
        this.toastr.success('Inspection Archived!');
        this.form.disable();
        if ([InspectionStatus.NO_DATE_ASSIGNED].includes(this.inspection.status)) {
          this.form.get('locations.dueDate').setValue(null);
          this.form.get('vendor').setValue(null);
          this.form.get('inspector').setValue(null);
        }
      },
    });
  }

  checkArchivedState() {
    if (this.inspection.archived) {
      this.form.disable();
    }
  }

  onInvoiceIssuedChange(ev: any) {
    this.pendingInvoiceIssued = ev.target.checked;
  }

  async requestRemove() {
    await this.inspectionService.remove(this.inspection.id);
    this.toastr.success('Inspection deleted!');
    await this.router.navigate(['/inspection']);
  }

  async remove(ev: Event) {
    ev.preventDefault();

    this.dialog.confirm().show({
      message: `Do you really want to permanently delete this inspection?`,
      confirmed: () => {
        if (this.inspection.status === InspectionStatus.INVALID_SYNC) {
          // confirm twice before deleting Invalid sync
          this.dialog.confirm().show({
            message: `All related information, including answers, will be lost. Are you sure?`,
            confirmed: () => {
              this.requestRemove();
            },
          });
        } else {
          // Go ahead and remove it
          this.requestRemove();
        }
      },
    });
  }

  // Email modals
  shouldShowEmailModal() {
    return this.sendEmailModal.availableEmails(this.inspection).length > 0;
  }

  showEmailModal(inspection) {
    this.sendEmailModal.show({ inspection });
  }

  // Form control
  disableFormExcept(controlsToEnable) {
    this.form.disable();
    controlsToEnable.forEach(control => this.form.get(control).enable());
  }

  getControlValidationClass(control) {
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }

  getValidationClass(field: string) {
    const control = this.form.get(field);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }

  getClass(tab: string) {
    return {
      'btn-primary': this.activeTab === tab,
      'btn-secondary': this.activeTab !== tab,
    };
  }

  @HostListener('window:beforeunload')
  canDeactivate() {
    const pristine = this.form.pristine;
    const iframeWindow = document.getElementsByTagName('iframe')[0]?.contentWindow as any;
    return !iframeWindow?.isUploading && pristine;
  }

  items() {
    return this.inspection.items.map(item => {
      return {
        name: item.item.name,
        quantity: item.quantity,
      };
    });
  }

  formatDate(dateString) {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  }

  async pdfFileChanged(file: File) {
    if (file) {
      if (file.name.toLowerCase().endsWith('.pdf')) {
        this.pdfFormData = new FormData();
        this.pdfFormData.append('upload', file);
      } else {
        console.log('Not PDF');
        this.pdfFileInput?.first?.nativeElement && (this.pdfFileInput.first.nativeElement.value = '');
      }
    }
  }

  async uploadSelectedPdf() {
    this.uploadingPdf = true;
    try {
      if (this.pdfFormData) {
        await this.inspectionService.uploadPdf(this.inspection.id, this.pdfFormData);
        this.boxIframe?.first?.nativeElement?.contentWindow?.location.reload();
        this.toastr.success(
          'PDF uploaded! The images are being processed and you will receive an email when it is ready.',
        );
      }
    } catch (e) {}

    this.pdfFileInput?.first?.nativeElement && (this.pdfFileInput.first.nativeElement.value = '');
    this.uploadingPdf = false;
  }

  getInitials(name: string): string {
    const words = name.trim().split(/\s+/);
    return words.length >= 2 ? words[0][0] + words[1][0] : words[0][0];
  }
}
